import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import { isValidEmail } from '@/utils/email';
import { useSignupFlow } from '../signup-flow';
import { SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { resolveStartSignupErrorMessage } from '../utils/start-signup-error-message';
import { useGoogleOneClickSignup, type GoogleSignupIdentity } from './use-google-one-click-signup';

const entryStepSchema = z.object({
  email: z.string().refine(isValidEmail, 'Enter a valid email address.'),
});

/**
 * Fires once per arrival on the entry step — both the email form and the
 * startup application variant.
 *
 * Clearing `isGoogleSignup` is the anti-circumvention reset: a Google signup
 * skips email verification, so coming back here must unbind it before a
 * different address can be submitted. Keyed on mount rather than on render, so
 * the Google flow can legitimately set it and keep it while the visitor
 * finishes on this step.
 */
export function useEntryStepArrival() {
  const { patchFormValues, trackSignupEvent, isStartup, effectiveSignupType } = useSignupFlow();
  const hasArrived = useRef(false);

  useEffect(() => {
    if (hasArrived.current) {
      return;
    }
    hasArrived.current = true;
    patchFormValues({ isGoogleSignup: false });
    trackSignupEvent(
      isStartup ? SignupAnalyticsEvent.StartupStepViewed : SignupAnalyticsEvent.Started,
      signupTypeProps({ signupType: effectiveSignupType }),
    );
  }, [patchFormValues, trackSignupEvent, isStartup, effectiveSignupType]);
}

/**
 * Whether to show the email error yet. Blur only counts once something has been
 * typed, and never unsets — an untouched visitor is not nagged, and one who has
 * been shown an error watches it clear as they correct the address.
 */
function useEmailErrorVisibility(submitCount: number) {
  const [hasBlurredWithValue, setHasBlurredWithValue] = useState(false);
  const markBlurred = useCallback((hasValue: boolean) => {
    setHasBlurredWithValue((wasBlurred) => wasBlurred || hasValue);
  }, []);
  return { shouldShowError: hasBlurredWithValue || submitCount > 0, markBlurred };
}

/** State and handlers for the entry step; the component is the markup. */
export function useEntryStepForm() {
  const { formValues, patchFormValues, beginSignup, trackSignupEvent } = useSignupFlow();
  const snackbars = useSnackbars();

  const form = useZodForm({
    schema: entryStepSchema,
    // Restores the prefilled or already-submitted address when navigating back
    // to the entry step remounts it.
    defaultValues: { email: formValues.email },
  });

  const startSignup = useCallback(
    async (email: string) => {
      try {
        await beginSignup(email);
      } catch (error) {
        snackbars.create({
          variant: 'error',
          id: 'signup.error',
          message: resolveStartSignupErrorMessage(error),
        });
        throw error;
      }
    },
    [beginSignup, snackbars],
  );

  const onGoogleIdentity = useCallback(
    ({ email, firstName, lastName }: GoogleSignupIdentity) => {
      patchFormValues({ email, firstName, lastName, isGoogleSignup: true });
      // The flow state and the form hold separate copies of the address; the
      // field reads the form's, so without this the visitor sees an empty box.
      form.setValue('email', email, { shouldValidate: true });
    },
    [patchFormValues, form],
  );

  const google = useGoogleOneClickSignup({ onIdentity: onGoogleIdentity, onContinue: startSignup });

  const onGoogleClick = useCallback(() => {
    // Only the click that actually starts a run counts: a second one landing
    // before the disabled state renders is suppressed, not a second attempt.
    if (google.start()) {
      trackSignupEvent(SignupAnalyticsEvent.SignUpWithGoogleClicked);
    }
  }, [trackSignupEvent, google]);

  const emailField = form.registerPluma('email');
  const emailError = form.formState.errors.email?.message;
  const { shouldShowError, markBlurred } = useEmailErrorVisibility(form.formState.submitCount);

  const onEmailInput = useCallback(
    (event: { target: { value: string } }) => {
      emailField.onInput(event);
      patchFormValues({ email: event.target.value });
    },
    [emailField, patchFormValues],
  );

  const onEmailBlur = useCallback(() => {
    emailField.onBlur();
    markBlurred(form.getValues('email') !== '');
  }, [emailField, markBlurred, form]);

  const onSubmit = form.handleSubmit(async ({ email }) => {
    // The snackbar is the visitor's feedback; the form just stays put.
    await startSignup(email).catch(() => {});
  });

  return {
    emailField,
    emailError,
    // The invalid styling is deliberately ungated while the message is not:
    // typing an address that isn't valid yet marks the field without nagging
    // before the visitor has finished.
    isEmailInvalid: emailError != null,
    visibleEmailError: shouldShowError ? emailError : undefined,
    onEmailInput,
    onEmailBlur,
    onSubmit,
    googleState: google.state,
    onGoogleClick,
    // Once Google has bound an identity the address is fixed: editing it would
    // let a different one inherit the skipped verification.
    isGoogleBound: google.state !== 'idle',
    isSubmitDisabled:
      (shouldShowError && emailError != null) || form.formState.isSubmitting || google.state === 'running',
    isSubmitting: form.formState.isSubmitting,
    submitLabel: formValues.isGoogleSignup === true ? 'Next: Set up Customer.io' : 'Next: Verify email',
  };
}
